import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Row,
  Col,
  Form,
  Spinner,
  Card,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import { getStudentDetails } from "../../actions/Student.action";
import { getStudentPayments } from "../../actions/Payment.action";
import { getGradeList } from "../../actions/Grade.action";
import styles from "../../components/ViewMarkForm/StudentView.module.css";
import "./StudentPaymentView.print.css";
import Moment from "react-moment";
import SectionHeading from "../../components/SectionHeading/SectionHeading";
import Layout from "../../components/shared/Layout/Layout";
import { BASE_URL } from "../../constants/URL";
import logo from "../../Assets/logo.jpeg";

const StudentPaymentViewPage = ({
  studentDetails,
  getStudentDetails,
  getGradeList,
  grades,
}) => {
  const { id } = useParams();
  const [year, setYear] = useState(new Date().getFullYear());
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  useEffect(() => {
    if (id) {
      getStudentDetails(id);
    }
  }, [id, getStudentDetails]);

  useEffect(() => {
    if (studentDetails && studentDetails.id) {
      fetchPayments();
    }
  }, [year, studentDetails]);

  const fetchPayments = async () => {
    if (!studentDetails || !studentDetails.id) return;

    setPaymentsLoading(true);
    try {
      const paymentsData = await getStudentPayments(studentDetails.id, year);
      // Sort payments by month and year
      const sortedPayments = paymentsData.sort((a, b) => {
        const months = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const monthA = months.indexOf(a.month);
        const monthB = months.indexOf(b.month);
        if (a.year !== b.year) return b.year - a.year;
        return monthB - monthA;
      });
      setPayments(sortedPayments);
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setPaymentsLoading(false);
      setLoading(false);
    }
  };

  const handleYearChange = (e) => {
    setYear(parseInt(e.target.value));
  };

  const totalAmount = payments.reduce((sum, payment) => {
    return sum + parseFloat(payment.total_amount || payment.amount || 0);
  }, 0);

  const studentName = studentDetails?.name || "Student";

  if (loading || !studentDetails) {
    return (
      <div
        className="bg_light"
        style={{ minHeight: "100vh", position: "relative" }}
      >
        <Layout title="Student Payment History">
          <Container
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <Spinner animation="border" variant="primary" />
          </Container>
        </Layout>
      </div>
    );
  }

  return (
    <div
      className="bg_light"
      style={{ minHeight: "100vh", position: "relative" }}
    >
      <Layout title={`${studentName} - Payment History`}>
        <Container fluid className="py-0">
          <div className="position-relative">
            {/* Year Selection - at the very top */}
            <Card className="mb-3 crd shadow">
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Select Year</Form.Label>
                      <Form.Select value={year} onChange={handleYearChange}>
                        {Array.from({ length: 20 }, (_, i) => i + 2010)
                          .reverse()
                          .map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {studentDetails?.image ? (
              <>
                <img src={logo} alt="logo" className={styles.logo} />
                <div
                  className={styles.logo2}
                  style={{
                    background: `url(${BASE_URL}/api/uploads/${studentDetails?.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                  }}
                ></div>
              </>
            ) : (
              <></>
            )}
            <SectionHeading
              noLogo={studentDetails?.image}
              title={``}
              subtitle={``}
              grade={studentDetails?.gradeId}
              shift={studentDetails?.shiftId}
              batch={studentDetails?.batchId}
            />

            {/* Print-friendly layout */}
            <div className="student-view-print">
              <div className="student-info-print">
                <Table bordered size="sm" responsive className="mb-0">
                  <tbody>
                    <tr>
                      {studentDetails && studentDetails.name && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Name</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.name}
                          </td>
                        </>
                      )}

                      {studentDetails && studentDetails.uid && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Serial</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.uid}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      {studentDetails &&
                        studentDetails.grade &&
                        studentDetails.grade.name && (
                          <>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <span className="fw-bold">Class</span>
                            </td>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {studentDetails.grade.name}
                            </td>
                          </>
                        )}

                      {studentDetails &&
                        studentDetails.shift &&
                        studentDetails.shift.name && (
                          <>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <span className="fw-bold">Shift</span>
                            </td>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {studentDetails.shift.name}
                            </td>
                          </>
                        )}
                    </tr>
                    <tr>
                      {studentDetails &&
                        studentDetails.batch &&
                        studentDetails.batch.name && (
                          <>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <span className="fw-bold">Batch</span>
                            </td>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {studentDetails.batch.name}
                            </td>
                          </>
                        )}

                      {studentDetails && studentDetails.phone && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Phone</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.phone}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      {studentDetails && studentDetails.father && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Father</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.father}
                          </td>
                        </>
                      )}
                      {studentDetails && studentDetails.mother && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Mother</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.mother}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      {studentDetails && studentDetails.dob && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Date of Birth</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <Moment format="DD/MM/YYYY">
                              {studentDetails.dob}
                            </Moment>
                          </td>
                        </>
                      )}
                      {studentDetails && studentDetails.blood && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Blood Group</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.blood}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      {studentDetails && studentDetails.address && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Address</span>
                          </td>
                          <td
                            colSpan={3}
                            className="py-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            {studentDetails.address}
                          </td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Fallback responsive layout for screen display */}
            <Row className="d-print-none">
              <Col md={12}>
                <Table bordered size="sm" responsive className="mb-0">
                  <tbody>
                    <tr>
                      {studentDetails && studentDetails.name && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Name</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.name}
                          </td>
                        </>
                      )}

                      {studentDetails && studentDetails.uid && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Roll</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.uid}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      {studentDetails &&
                        studentDetails.grade &&
                        studentDetails.grade.name && (
                          <>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <span className="fw-bold">Class</span>
                            </td>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {studentDetails.grade.name}
                            </td>
                          </>
                        )}

                      {studentDetails &&
                        studentDetails.shift &&
                        studentDetails.shift.name && (
                          <>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <span className="fw-bold">Shift</span>
                            </td>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {studentDetails.shift.name}
                            </td>
                          </>
                        )}
                    </tr>
                    <tr>
                      {studentDetails &&
                        studentDetails.batch &&
                        studentDetails.batch.name && (
                          <>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <span className="fw-bold">Batch</span>
                            </td>
                            <td
                              className="py-1"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {studentDetails.batch.name}
                            </td>
                          </>
                        )}

                      {studentDetails && studentDetails.phone && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Phone</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.phone}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      {studentDetails && studentDetails.father && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Father</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.father}
                          </td>
                        </>
                      )}
                      {studentDetails && studentDetails.mother && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Mother</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.mother}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      {studentDetails && studentDetails.dob && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Date of Birth</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <Moment format="DD/MM/YYYY">
                              {studentDetails.dob}
                            </Moment>
                          </td>
                        </>
                      )}
                      {studentDetails && studentDetails.blood && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Blood Group</span>
                          </td>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            {studentDetails.blood}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      {studentDetails && studentDetails.address && (
                        <>
                          <td className="py-1" style={{ fontSize: "0.75rem" }}>
                            <span className="fw-bold">Address</span>
                          </td>
                          <td
                            colSpan={3}
                            className="py-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            {studentDetails.address}
                          </td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Payments Table */}
            <Card className="mt-4 crd shadow">
              <Card.Header>
                <h5 className="mb-0">Payment Entries for {year}</h5>
              </Card.Header>
              <Card.Body>
                {paymentsLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: 200 }}
                  >
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : payments.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">
                      No payment entries found for {year}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <strong>
                        Total Service Charge: {totalAmount.toFixed(2)}
                      </strong>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <Table striped bordered hover responsive className="mb-0">
                        <thead>
                          <tr>
                            <th>Month</th>
                            <th>Year</th>
                            <th>Service Charge</th>
                            <th>Session Charge/ Extra Cost</th>
                            <th>Admission Fee/ Exam Fee</th>
                            <th>Total</th>
                            <th>Note</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((payment, index) => (
                            <tr key={payment.id || index}>
                              <td>{payment.month}</td>
                              <td>{payment.year}</td>
                              <td>
                                {parseFloat(payment.amount || 0).toFixed(2)}
                              </td>
                              <td>
                                {parseFloat(payment.extra_amount || 0).toFixed(
                                  2,
                                )}
                              </td>
                              <td>
                                {parseFloat(payment.exam_fee || 0).toFixed(2)}
                              </td>
                              <td>
                                {parseFloat(
                                  payment.total_amount || payment.amount || 0,
                                ).toFixed(2)}
                              </td>
                              <td>{payment.note || "-"}</td>
                              <td>
                                {payment.createdAt ? (
                                  <Moment format="DD/MM/YYYY">
                                    {payment.createdAt}
                                  </Moment>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan="4" className="text-end fw-bold">
                              Total:
                            </td>
                            <td className="fw-bold">
                              {totalAmount.toFixed(2)}
                            </td>
                            <td colSpan="2"></td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
        </Container>
      </Layout>
    </div>
  );
};

const mapStateToProps = (state) => ({
  studentDetails: state.student.selected_student,
  grades: state.grade.grade,
});

export default connect(mapStateToProps, {
  getStudentDetails,
  getStudentPayments,
  getGradeList,
})(StudentPaymentViewPage);
